import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Stack,
  Carousel,
} from "react-bootstrap";

/**
 * PowerWashingLanding_v05_Carousel
 * ----------------------------------------------------------------------------
 * Date/Time (America/Denver): Wednesday, August 27, 2025 at 10:00:00 AM
 * Chat URL: PASTE_THE_URL_OF_THIS_CHAT_HERE
 *
 * Summary
 * - Background hero uses react-bootstrap Carousel with slide-specific title/subline.
 * - The left hero headline/subline synchronizes with the active slide; each slide also
 *   renders a small caption on the image for clarity.
 * - Quote form aligned to the right on desktop; validated fields and test-mode logging.
 * - Mobile-first layout with stacked content.
 *
 * Functional Features
 * - Quick Quote form fields: first/last name, phone, email, full address, ZIP, service type,
 *   surfaces, estimated size, stain/soiling level, preferred date/time, eco-friendly option,
 *   notes, consent checkbox.
 * - Test mode prints payload with meta to console; otherwise, POSTs to API_URL.
 * - Easily swap images: update the `heroSlides` array.
 */

// ---------- Config & Meta ----------
const CLIENT_VERSION = "v05a" as const; // 2025-08-27
const TEST_MODE = true; // set false to POST to API_URL
const API_URL = "https://api.example.com/powerwashing/quote";

// Relative path or CDN root for images
const imagePath = "powerwashing/images/HERO/";

// ---------- Types ----------
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
}

interface LeadPayload {
  meta: {
    clientVersion: typeof CLIENT_VERSION;
    submittedAtISO: string;
    page: string;
  };
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  address: string;
  zip: string;
  serviceType: "House Wash" | "Driveway/Sidewalk" | "Roof Cleaning" | "Deck/Fence" | "Commercial";
  surfaces: string[]; // e.g., ["Siding", "Concrete", "Pavers"]
  estimatedSize: {
    unit: "sqft" | "linearft";
    value?: number;
  };
  soilLevel: "Light" | "Moderate" | "Heavy";
  preferredDate?: string; // yyyy-mm-dd
  preferredTime?: string; // HH:MM
  ecoFriendly: boolean;
  notes?: string;
  consent: boolean;
}

// ---------- Content ----------
const heroSlides: Slide[] = [
  {
    src: imagePath + "powerwash-driveway-01.jpg",
    alt: "Pressure washing a driveway",
    title: "Make Your Curb Appeal Pop",
    subline: "Professional driveway & sidewalk cleaning that lasts.",
  },
  {
    src: imagePath + "house-wash-02.jpg",
    alt: "Soft washing a home exterior",
    title: "Gentle on Siding. Tough on Grime.",
    subline: "Soft-wash house cleaning for vinyl, brick, and stucco.",
  },
  {
    src: imagePath + "deck-fence-03.jpg",
    alt: "Cleaning a wood deck",
    title: "Decks & Fences, Like New Again",
    subline: "Restore wood surfaces safely and beautifully.",
  },
  {
    src: imagePath + "roof-clean-04.jpg",
    alt: "Removing roof algae and streaks",
    title: "Banish Roof Stains",
    subline: "Treat black streaks and algae the right way.",
  },
  {
    src: imagePath + "commercial-plaza-05.jpg",
    alt: "Washing a commercial plaza walkway",
    title: "Commercial Shine, Customer Ready",
    subline: "Plazas • Restaurants • Storefronts • Parking pads",
  },
];

const BENEFITS: { id: string; title: string; items: string[] }[] = [
  {
    id: "quality",
    title: "Why Choose Us",
    items: [
      "Licensed & Insured technicians",
      "Eco-friendly detergents available",
      "Modern equipment with proper nozzles & tips",
      "Soft wash for delicate surfaces; pressure where appropriate",
      "Upfront, written estimates—no surprises",
    ],
  },
  {
    id: "results",
    title: "Results You Can See",
    items: [
      "Removes algae, mold, mildew, oil, and rust stains",
      "Brightens concrete & paver joints",
      "Preps surfaces for paint or stain",
      "Boosts curb appeal & property value",
    ],
  },
  {
    id: "services",
    title: "Popular Services",
    items: [
      "House soft wash",
      "Driveway & sidewalk cleaning",
      "Roof algae treatment",
      "Deck & fence restoration",
      "Commercial storefront maintenance",
    ],
  },
];

// ---------- Component ----------
export const PowerWashingLanding_v05_Carousel: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<null | { variant: "success" | "danger"; message: string }>(null);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [serviceType, setServiceType] = useState<LeadPayload["serviceType"]>("House Wash");
  const [surfaces, setSurfaces] = useState<string[]>([]);
  const [estimatedUnit, setEstimatedUnit] = useState<LeadPayload["estimatedSize"]["unit"]>("sqft");
  const [estimatedValue, setEstimatedValue] = useState<number | undefined>(undefined);
  const [soilLevel, setSoilLevel] = useState<LeadPayload["soilLevel"]>("Moderate");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [ecoFriendly, setEcoFriendly] = useState(false);
  const [notes, setNotes] = useState("");
  const [consent, setConsent] = useState(false);

  const canSubmit = useMemo(() => {
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      phone.trim().length > 0 &&
      address.trim().length > 0 &&
      zip.trim().length >= 5 &&
      consent
    );
  }, [firstName, lastName, phone, address, zip, consent]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    if (!canSubmit) {
      setStatus({ variant: "danger", message: "Please complete required fields and consent to be contacted." });
      return;
    }
    setSubmitting(true);

    const payload: LeadPayload = {
      meta: {
        clientVersion: CLIENT_VERSION,
        submittedAtISO: new Date().toISOString(),
        page: "PowerWashingLanding_v05_Carousel",
      },
      firstName,
      lastName,
      phone,
      email: email || undefined,
      address,
      zip,
      serviceType,
      surfaces,
      estimatedSize: {
        unit: estimatedUnit,
        value: estimatedValue || undefined,
      },
      soilLevel,
      preferredDate: preferredDate || undefined,
      preferredTime: preferredTime || undefined,
      ecoFriendly,
      notes: notes || undefined,
      consent,
    };

    try {
      if (TEST_MODE) {
        // Simulate network delay
        await new Promise((r) => setTimeout(r, 700));
        // Log test payload
        // eslint-disable-next-line no-console
        console.log("[TEST_MODE] Power washing lead payload:", payload);
        setStatus({ variant: "success", message: "Thanks! Your request is recorded (test mode)." });
      } else {
        const res = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        setStatus({ variant: "success", message: "Thanks! We\'ll be in touch shortly." });
      }
      // Reset minimal fields on success
      setNotes("");
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error(err);
      setStatus({ variant: "danger", message: "Sorry—something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  function toggleSurface(surface: string) {
    setSurfaces((prev) => (prev.includes(surface) ? prev.filter((s) => s !== surface) : [...prev, surface]));
  }

  return (
    <div>
      {/* Top Navbar */}
      <Navbar bg="light" expand="md" sticky="top" className="border-bottom">
        <Container>
          <Navbar.Brand className="fw-bold">
            <span className="text-primary">Mile High</span> Power Washing
            <Badge bg="secondary" className="ms-2">{CLIENT_VERSION}</Badge>
          </Navbar.Brand>
          <Nav className="ms-auto d-none d-md-flex">
            <Nav.Link href="#services">Services</Nav.Link>
            <Nav.Link href="#benefits">Benefits</Nav.Link>
            <Nav.Link href="#quote">Get a Quote</Nav.Link>
          </Nav>
        </Container>
      </Navbar>

      {/* HERO SECTION */}
      <section id="home" className="position-relative" style={{ minHeight: "72vh", overflow: "hidden" }}>
        {/* Background carousel */}
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 0 }}>
          <Carousel activeIndex={activeIndex} onSelect={(i) => setActiveIndex(i)} fade controls indicators>
            {heroSlides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  className="w-100 h-100"
                  style={{
                    position: "relative",
                    backgroundImage: `url(${s.src})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    minHeight: "72vh",
                  }}
                >
                  {/* Small in-image caption */}
                  <div className="position-absolute bottom-0 start-0 m-3 p-2 rounded bg-dark bg-opacity-50 text-white">
                    <div className="fw-semibold small">{s.title}</div>
                    <div className="small- text-white-50">{s.subline}</div>
                  </div>
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
          {/* Dark gradient overlay for contrast */}
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.15) 100%)",
            }}
          />
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="py-4 py-md-5 align-items-stretch" xs={1} md={2}>
            {/* Left: Hero text that syncs with slide */}
            <Col className="text-white d-flex align-items-center">
              <div className="py-4 py-md-0">
                <Badge bg="warning" text="dark" className="mb-3">DENVER • BOULDER • FRONT RANGE</Badge>
                <h1 className="display-5 fw-bold">{heroSlides[activeIndex]?.title}</h1>
                <p className="lead mb-4" style={{ maxWidth: 640 }}>{heroSlides[activeIndex]?.subline}</p>
                <Stack direction="horizontal" gap={2} className="flex-wrap">
                  <Badge bg="success">Licensed & Insured</Badge>
                  <Badge bg="info">Soft Wash & Pressure</Badge>
                  <Badge bg="primary">Eco Option</Badge>
                  <Badge bg="light" text="dark">Free Estimates</Badge>
                </Stack>
              </div>
            </Col>

            {/* Right: Quote Form */}
            <Col className="d-flex">
              <Card id="quote" className="ms-md-auto shadow-lg my-3 my-md-0" style={{ maxWidth: 520, width: "100%" }}>
                <Card.Header className="bg-primary text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Get a Fast Quote</span>
                    <Badge bg="light" text="dark">~2 min</Badge>
                  </div>
                </Card.Header>
                <Card.Body>
                  {status && (
                    <Alert variant={status.variant} onClose={() => setStatus(null)} dismissible>
                      {status.message}
                    </Alert>
                  )}

                  <Form onSubmit={onSubmit} className="gy-3">
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Label htmlFor="firstName">First Name *</Form.Label>
                        <Form.Control id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="lastName">Last Name *</Form.Label>
                        <Form.Control id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Label htmlFor="phone">Phone *</Form.Label>
                        <Form.Control id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="email">Email</Form.Label>
                        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="optional" />
                      </Col>
                    </Row>

                    <Form.Label htmlFor="address">Service Address *</Form.Label>
                    <Form.Control id="address" value={address} onChange={(e) => setAddress(e.target.value)} required placeholder="123 Main St, City, CO" />

                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Label htmlFor="zip">ZIP Code *</Form.Label>
                        <Form.Control id="zip" value={zip} onChange={(e) => setZip(e.target.value)} required />
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="serviceType">Service Type</Form.Label>
                        <Form.Select id="serviceType" value={serviceType} onChange={(e) => setServiceType(e.target.value as LeadPayload["serviceType"]) }>
                          <option>House Wash</option>
                          <option>Driveway/Sidewalk</option>
                          <option>Roof Cleaning</option>
                          <option>Deck/Fence</option>
                          <option>Commercial</option>
                        </Form.Select>
                      </Col>
                    </Row>

                    {/* Surfaces */}
                    <Form.Label className="mt-2">Surfaces</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {[
                        "Siding",
                        "Concrete",
                        "Pavers",
                        "Brick",
                        "Stucco",
                        "Wood",
                        "Roof",
                      ].map((s) => (
                        <Form.Check
                          key={s}
                          type="checkbox"
                          id={`surface-${s}`}
                          label={s}
                          checked={surfaces.includes(s)}
                          onChange={() => toggleSurface(s)}
                        />
                      ))}
                    </div>

                    {/* Size & Soil */}
                    <Row className="g-2 mt-1">
                      <Col md={6}>
                        <Form.Label htmlFor="estimatedValue">Estimated Size</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            id="estimatedValue"
                            type="number"
                            min={0}
                            value={typeof estimatedValue === "number" ? estimatedValue : ""}
                            onChange={(e) => setEstimatedValue(e.target.value ? Number(e.target.value) : undefined)}
                            placeholder="e.g. 1200"
                            aria-describedby="sizeHelp"
                          />
                          <Form.Select
                            value={estimatedUnit}
                            onChange={(e) => setEstimatedUnit(e.target.value as LeadPayload["estimatedSize"]["unit"]) }
                            style={{ maxWidth: 120 }}
                          >
                            <option value="sqft">sq ft</option>
                            <option value="linearft">linear ft</option>
                          </Form.Select>
                        </div>
                        <Form.Text id="sizeHelp">If unsure, leave blank.</Form.Text>
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="soilLevel">Stain/Soiling Level</Form.Label>
                        <Form.Select id="soilLevel" value={soilLevel} onChange={(e) => setSoilLevel(e.target.value as LeadPayload["soilLevel"]) }>
                          <option>Light</option>
                          <option>Moderate</option>
                          <option>Heavy</option>
                        </Form.Select>
                      </Col>
                    </Row>

                    {/* Date / Time */}
                    <Row className="g-2 mt-1">
                      <Col md={6}>
                        <Form.Label htmlFor="preferredDate">Preferred Date</Form.Label>
                        <Form.Control id="preferredDate" type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} />
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor="preferredTime">Preferred Time</Form.Label>
                        <Form.Control id="preferredTime" type="time" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} />
                      </Col>
                    </Row>

                    <Form.Check
                      className="mt-2"
                      id="eco"
                      type="switch"
                      label="I'd like an eco-friendly detergent option if suitable"
                      checked={ecoFriendly}
                      onChange={(e) => setEcoFriendly(e.currentTarget.checked)}
                    />

                    <Form.Label htmlFor="notes" className="mt-2">Notes</Form.Label>
                    <Form.Control
                      id="notes"
                      as="textarea"
                      rows={3}
                      placeholder="Anything we should know? Gate codes, pets, stains, special requests..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />

                    <Form.Check
                      className="mt-3"
                      id="consent"
                      type="checkbox"
                      label="I consent to be contacted about this request."
                      checked={consent}
                      onChange={(e) => setConsent(e.currentTarget.checked)}
                      required
                    />

                    <div className="d-grid mt-3">
                      <Button type="submit" variant="primary" disabled={!canSubmit || submitting}>
                        {submitting ? "Submitting..." : "Get My Quote"}
                      </Button>
                    </div>

                    <Form.Text className="text-muted">
                      By submitting, you agree to our terms and privacy policy. Typical response time: same day.
                    </Form.Text>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Professional Power Washing, Done Right</h2>
              <p className="text-muted m-0">Serving Denver, Boulder, Longmont, and the Front Range.</p>
            </Col>
          </Row>

          <Row xs={1} md={3} className="g-4">
            {BENEFITS.map((b) => (
              <Col key={b.id}>
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-white">
                    <span className="fw-semibold">{b.title}</span>
                  </Card.Header>
                  <Card.Body>
                    <ul className="m-0 ps-3">
                      {b.items.map((it, i) => (
                        <li key={i} className="mb-1">{it}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* SERVICES STRIP */}
      <section id="services" className="py-4 bg-white border-top">
        <Container>
          <Row className="text-center g-3" xs={2} md={5}>
            {[
              "House Wash",
              "Driveway",
              "Sidewalks",
              "Decks/Fences",
              "Roofs",
            ].map((t) => (
              <Col key={t}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="fw-bold">{t}</div>
                    <div className="text-muted small">Available now</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <footer className="py-4 bg-dark text-white-50 mt-4">
        <Container>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
            <div>
              © {new Date().getFullYear()} Mile High Power Washing · All rights reserved
            </div>
            <div className="small">
              Build {CLIENT_VERSION} · React + React-Bootstrap · Carousel Hero
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default PowerWashingLanding_v05_Carousel;
